<template>
  <CollapsableSection
    :class="$style.labelWeight"
    :collapse="!isItemBeingCreated"
    title="User Security Settings and Permissions"
  >
    <ChildForm
      label="Password Recovery Email"
      :tooltip="passwordRecoveryEmailTooltip"
    >
      <PasswordRecoveryEmail
        :companyEmail="item.email"
        data-test-id="passwordRecoveryEmail"
        :handleValueChange="handleValueChange"
        :recoveryEmail="item.recoveryEmail"
      />
    </ChildForm>
    <ChildForm
      :class="{[$style.rowSpacing]: !showSpecifyPassword}"
      label="Password Settings"
    >
      <div
        v-if="item.externally_managed"
        :class="[$style.inputRow, $style.rowSpacing]"
      >
        <ExternalManagementDetails
          :class="$style.flexSingleWidth"
          data-test-id="externalManagementDetails"
          :parentType="parentType"
        />
      </div>
      <div
        v-if="showSpecifyPassword"
        :class="[$style.inputRow, $style.rowSpacing]"
      >
        <SpecifyPassword
          :class="$style.flexSingleWidth"
          data-test-id="specifyPassword"
          :parentType="parentType"
          :valueChange="handleValueChange"
          :workflow="isItemBeingCreated ? $options.create : $options.update"
        />
      </div>
      <div
        v-if="!item.externally_managed && !showSpecifyPassword"
      >
        <p
          data-test-id="passwordSettingsNotAvailable"
        >
          Password Settings are not currently available for this user.
        </p>
      </div>
    </ChildForm>
    <ChildForm
      v-if="showAccountLockout"
      label="Account lockout threshold for devices"
    >
      <AccountLockout
        :data-test-id="$testId('accountLockout')"
        :value="item.disableDeviceMaxLoginAttempts"
        @change="handleValueChange($event, 'disableDeviceMaxLoginAttempts')"
      />
    </ChildForm>
    <ChildForm
      label="Multi-factor Authentication Settings"
    >
      <div :class="[$style.inputRow, $style.rowSpacing]">
        <div :class="[$style.flexSingleWidth]">
          <UserMFAStatus
            :parentType="parentType"
            :valueChange="handleValueChange"
          />
        </div>
      </div>
    </ChildForm>
    <ChildForm
      v-if="isItemBeingUpdated"
      :class="$style.rowSpacing"
      label="JumpCloud Protect Mobile Push Status"
      :tooltip="jcProtectTooltip"
    >
      <JCProtectDeviceTable />
    </ChildForm>
    <template
      v-if="isItemBeingUpdated"
    >
      <WebAuthnKeys
        v-if="$hasFeatureFlag($featureFlags.platformAuthenticator)"
      />
      <ChildForm
        v-else
        :class="$style.rowSpacing"
        label="WebAuthn Security Keys"
        :tooltip="webAuthnTooltip"
      >
        <WebAuthnKeysLegacy />
      </ChildForm>
    </template>
    <ChildForm
      label="Permission Settings"
    >
      <div :class="[$style.inputRow, $style.rowSpacing, $style.permissions]">
        <SudoInput
          :class="[$style.flexSingleWidth]"
          :parentType="parentType"
          :valueChange="handleValueChange"
        />
        <span
          :class="$style.groupSudoInfo"
        >
          Permissions can now be controlled at the group level.
          <router-link :to="{ name: 'UserGroups' }"> Check it out.</router-link>
        </span>
      </div>
      <div :class="[$style.inputRow, $style.rowSpacing]">
        <div :class="$style.flexSingleWidth">
          <LdapBindDnInput
            :parentType="parentType"
          />
        </div>
      </div>
      <div
        :class="$style.inputRow"
      >
        <div :class="$style.flexSingleWidth">
          <UIDAndGIDConsistency
            v-if="enableManagedUIDOrgWide === true"
            :enableManagedUIDForUser="item.enable_managed_uid === true"
            :handleValueChange="handleValueChange"
            :parentType="parentType"
            :unixGID="item.unix_guid"
            :unixUID="item.unix_uid"
          />
        </div>
      </div>
    </ChildForm>
  </CollapsableSection>
</template>

<script>
import { ItemActionConsts, ModuleConsts } from 'client/util/Constants';
import { get } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import AccountLockout from 'client/systemusers/UserDetails/AccountLockout/AccountLockout.vue';
import AsidePanelChildComponentMethodsMixin from 'components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import ChildForm from 'components/FormValidation/ChildForm.vue';
import CollapsableSection from 'components/ContentSection/CollapsableSection.vue';
import ExternalManagementDetails from 'client/systemusers/UserDetails/ExternalManagementDetails.vue';
import JCProtectDeviceTable from 'client/systemusers/UserDetails/JCProtectDeviceTable.vue';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import LdapBindDnInput from 'client/systemusers/UserDetails/LdapBindDnInput.vue';
import NavPanelLinkList from 'client/components/NavPanel/NavPanelLinkList';
import PasswordRecoveryEmail from 'client/systemusers/UserDetails/PasswordRecoveryEmail.vue';
import SpecifyPassword from 'client/systemusers/UserDetails/SpecifyPassword/SpecifyPassword.vue';
import SudoInput from 'client/systemusers/UserDetails/SudoInput.vue';
import UIDAndGIDConsistency from 'client/systemusers/UserDetails/UIDAndGIDConsistency.vue';
import UserMFAStatus from 'client/systemusers/UserDetails/UserMFAStatus.vue';
import WebAuthnKeys from 'client/systemusers/UserDetails/WebAuthnKeys';
import WebAuthnKeysLegacy from 'client/systemusers/UserDetails/WebAuthnKeysLegacy.vue';

const systemUserModel = 'SystemUserModel';

const { mfaLink } = NavPanelLinkList;
const { activated, staged } = ModuleConsts.systemUser.accountStates;

export default {
  name: 'UserSecurityForm',

  components: {
    AccountLockout,
    ChildForm,
    CollapsableSection,
    ExternalManagementDetails,
    JCProtectDeviceTable,
    LdapBindDnInput,
    PasswordRecoveryEmail,
    SpecifyPassword,
    SudoInput,
    UserMFAStatus,
    UIDAndGIDConsistency,
    WebAuthnKeys,
    WebAuthnKeysLegacy,
  },

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
  ],

  inject: ['$validator'],

  props: {
    handleValueChange: {
      type: Function,
      required: true,
    },
  },

  computed: {
    ...mapGetters(systemUserModel, ['isRestrictedField']),

    ...mapState('SettingsModule', {
      enableManagedUIDOrgWide: 'enableManagedUID',
      newSystemUserStateDefaults: (state) => state.newSystemUserStateDefaults,
    }),

    enableUserPortalMultifactor: {
      get() {
        return this.item.enable_user_portal_multifactor;
      },
      set(value) {
        return this.handleValueChange(
          value,
          'enable_user_portal_multifactor',
        );
      },
    },

    showAccountLockout() {
      return this.showSpecifyPassword;
    },

    /* eslint-disable max-len */
    /**
     * | Conditions            |        |        |           |        |        |        |        |        |        |        |           |           |
     * |-----------------------|--------|--------|-----------|--------|--------|--------|--------|--------|--------|--------|-----------|-----------|
     * | workflow              | create | create |   create  | create | create | update | update | update | update | update |   update  |   update  |
     * | externally_managed    |    T   |    F   |     F     |    F   |    F   |    T   |    T   |    F   |    F   |    F   |     F     |     -     |
     * | password restricted   |    -   |    -   |     -     |    -   |    -   |    T   |    F   |    F   |    T   |    -   |     -     |     -     |
     * | item.state            |    -   |    -   |     -     |    -   |    -   |    -   |    -   |    -   |    -   | staged | activated | suspended |
     * | manualEntry           |    -   | staged | activated |    -   |    -   |    -   |    -   |    -   |    -   |    -   |     -     |     -     |
     * |                       |        |        |           |        |        |        |        |        |        |        |           |           |
     * | Actions               |        |        |           |        |        |        |        |        |        |        |           |           |
     * |-----------------------|--------|--------|-----------|--------|--------|--------|--------|--------|--------|--------|-----------|-----------|
     * | Show Specify Password |    F   |    T   |     T     |    F   |    T   |    F   |    T   |    T   |    F   |    T   |     T     |     F     |
     * /
    /* eslint-enable max-len */
    showSpecifyPassword() {
      let show = false;

      const restricted = this.item.externally_managed || this.isRestrictedField('password');

      switch (this.isItemBeingCreated) {
        case true:
          if (!restricted) {
            show = true;
          }

          if (!restricted) {
            const state = get(this.newSystemUserStateDefaults, 'manualEntry', '');
            if (state === activated || state === staged) {
              show = true;
            }
          }
          break;
        case false:
          show = !this.isRestrictedField('password') && (this.item.state === activated || this.item.state === staged);
          break;
        default:
          break;
      }

      return show;
    },
  },

  beforeCreate() {
    this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options);
  },

  /**
   * Add non-reactive data to component in created hook.
   */
  created() {
    this.webAuthnTooltip = {
      text: 'Add the User’s WebAuthn Security Keys. The user needs a registered key to log into the User Portal with WebAuthn MFA. Enable WebAuthn MFA in the Org Settings MFA Configuration section.',
      kbLink: KbArticleUrls.webauthn,
      kbLinkText: 'Read the Knowledge Base',
    };
    this.jcProtectTooltip = {
      text: `JumpCloud Protect verifies logins with push notifications that are sent to users mobile devices. When you enable JumpCloud Protect, users with MFA already set up aren’t impacted. This must be enabled on the <a href="${mfaLink.href}">${mfaLink.title}</a> page.`,
      kbLink: KbArticleUrls.jcProtectForAdmins,
      kbLinkText: 'Read the Knowledge Base',
    };
    this.passwordRecoveryEmailTooltip = {
      text: 'The password recovery email enables users to have another email in addition to their organization email for password resets. Password reset emails will be sent to both email addresses. Recovery Email will always be editable by users in the User Portal.',
      kbLink: KbArticleUrls.securitySettingsPassword,
      kbLinkText: 'Read the Knowledge Base',
    };
  },

  mounted() {
    this.handleMounted();
  },

  methods: {
    ...mapActions('WebAuthnModule', {
      initializeWebAuthn: 'initialize',
    }),

    handleMounted() {
      this.initializeWebAuthn();
    },
  },

  create: ItemActionConsts.create,
  update: ItemActionConsts.update,
};
</script>

<style module>
@import '../../css/flex.css';
@import '../../components/FormValidation/css/formFields.css';

.rowSpacing {
  margin-bottom: 20px;
  margin-top: 0;
}

.coupletError {
  width: 100vw;
}

.errorSpacing {
  margin-bottom: 10px;
}

.specifyPassword {
  margin-bottom: 1rem;
  outline: 2px dotted pink;
}

.permissions {
  flex-wrap: wrap;
}

.groupSudoInfo {
  font-size: 11px;
  margin-left: var(--jc-spacer-medium);
  width: 100%;
}
</style>
